import {observer} from "mobx-react";
import React, {useMemo} from "react";

import {DataType} from "../../../stores/database-template/database-form-base";
import {bindInput} from "../../../utils/bind-input";
import {AddButton, SaveButton} from "../../atoms/buttons/buttons";
import {
    ActionsColumn,
    FieldsColumn,
    HeaderGrid,
    InputWidthWrapper,
    PageContainer,
} from "../../atoms/layouts/category-layout-containers";
import {FormField} from "../../molecules/inputs/form-field";
import {SubmitGroup} from "../../molecules/submit-group/submit-group";
import DatabaseRow from "../../molecules/tables/database-row";
import {AdminBreadcrumb} from "../../organisms/admin-breadcrumb/admin-breadcrumb";
import {TableLayout} from "../../organisms/table-layout/table-layout";
import {AppLayout} from "../../templates/app-layout/app-layout";
import {DatabaseTemplateCreatePageStore} from "./database-template-create-store";

// Props 型定義
type Props = {pageStore: DatabaseTemplateCreatePageStore};

type Handlers = {
    onSlugChange: (id: string, value: string) => void;
    onLabelChange: (id: string, value: string) => void;
    onDataTypeChange: (id: string, value: DataType) => void;
    onDescriptionChange: (id: string, value: string) => void;
    onRemove: (id: string) => void;
};

// 1) ヘッダー部：フォーム入力＆追加/保存
const TemplateHeader = observer(({store}: {store: DatabaseTemplateCreatePageStore}) => (
    <HeaderGrid>
        <FieldsColumn aria-label="テンプレート情報">
            <InputWidthWrapper>
                <FormField
                    label="テンプレート名"
                    required
                    value={store.templateName}
                    errorText={store.templateNameError}
                    placeholder="テンプレート名を入力"
                    {...bindInput(store.setTemplateName)}
                />
            </InputWidthWrapper>
            <InputWidthWrapper width={500}>
                <FormField
                    label="テンプレートの説明"
                    value={store.templateNote}
                    errorText={store.templateNoteError}
                    placeholder="テンプレートの説明を入力"
                    {...bindInput(store.setTemplateNote)}
                />
            </InputWidthWrapper>
        </FieldsColumn>
        <ActionsColumn>
            <AddButton onClick={store.addTemplateRow}>項目の追加</AddButton>
            <SaveButton disabled={!store.canCreate} onClick={store.createTemplate}>
                保存
            </SaveButton>
        </ActionsColumn>
    </HeaderGrid>
));

// 2) テーブル行部：各行を監視（DatabaseRow は内部で observerされている）
const TemplateCreateTable = observer(
    ({store, handlers}: {store: DatabaseTemplateCreatePageStore; handlers: Handlers}) => (
        <TableLayout areaLabel="テンプレート新規作成" columns={store.DEFAULT_COLUMN_DEFS}>
            {store.templateColumnList.map((row) => (
                <DatabaseRow
                    key={row.id}
                    {...row}
                    {...handlers}
                    noteError={row.noteError}
                    canRemove={store.templateColumnList.length > 1}
                />
            ))}
        </TableLayout>
    ),
);

// 3) メインコンポーネント：observer を外し、必要箇所のみ再レンダー
const DatabaseTemplateCreatePage: React.FC<Props> = ({pageStore}) => {
    const handlers = useMemo<Handlers>(
        () => ({
            onSlugChange: pageStore.setSlug,
            onLabelChange: pageStore.setLabel,
            onDataTypeChange: pageStore.setDataTypeSelect,
            onDescriptionChange: pageStore.setNote,
            onRemove: pageStore.removeTemplateRow,
        }),
        [pageStore],
    );

    return (
        <AppLayout
            breadcrumb={
                <AdminBreadcrumb
                    factory={(b) => b.databaseTemplates().custom("新規作成").build()}
                />
            }
        >
            <SubmitGroup
                onSubmit={() => pageStore.createTemplate()}
                canSubmit={pageStore.canCreate}
            >
                <PageContainer>
                    <TemplateHeader store={pageStore} />
                    <TemplateCreateTable store={pageStore} handlers={handlers} />
                </PageContainer>
            </SubmitGroup>
        </AppLayout>
    );
};

export default DatabaseTemplateCreatePage;
